import { useMemo, useState, useRef, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Home } from 'lucide-react'

interface CityVisualizationProps {
  totalHouses: number
  userHouses: number
  projectName: string
}

interface HousePosition {
  id: number
  x: number
  y: number
  isUser: boolean
  height: number
  width: number
}

export const CityVisualization = ({ totalHouses, userHouses, projectName }: CityVisualizationProps) => {
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  const housePositions = useMemo(() => {
    const positions: HousePosition[] = []
    
    // Create array of house indices and shuffle to randomize user houses
    const houseIndices = Array.from({ length: totalHouses }, (_, i) => i)
    const shuffled = [...houseIndices].sort(() => Math.random() - 0.5)
    const userHouseIndices = new Set(shuffled.slice(0, userHouses))

    // Create centered horizontal arrangement like in reference image
    for (let i = 0; i < totalHouses; i++) {
      // Arrange houses in a centered horizontal line with slight clustering
      const houseSpacing = Math.min(60 / totalHouses, 8) // Limit spacing for many houses
      const startX = 50 - (totalHouses - 1) * houseSpacing / 2 // Center the group
      const baseX = startX + i * houseSpacing
      
      // Add slight vertical and horizontal randomness while keeping centered
      const x = Math.max(20, Math.min(80, baseX + (Math.random() - 0.5) * 4))
      const y = Math.max(45, Math.min(55, 50 + (Math.random() - 0.5) * 6)) // Keep vertically centered
      
      positions.push({
        id: i,
        x,
        y,
        isUser: userHouseIndices.has(i),
        height: 40 + Math.random() * 20,
        width: 35 + Math.random() * 15
      })
    }

    return positions
  }, [totalHouses, userHouses])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true)
    setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y })
  }, [panOffset])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return
    setPanOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }, [isDragging, dragStart])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    setIsDragging(true)
    setDragStart({ x: touch.clientX - panOffset.x, y: touch.clientY - panOffset.y })
  }, [panOffset])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return
    const touch = e.touches[0]
    setPanOffset({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    })
  }, [isDragging, dragStart])

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2 text-blue-800">
          <Home className="h-6 w-6" />
          {projectName}
        </CardTitle>
        <CardDescription className="text-blue-700">
          Gesamt: {totalHouses} Familien unterstützt • Durch Ihre Hilfe: {userHouses} Familien
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div 
          ref={containerRef}
          className={`relative bg-orange-50 rounded-lg p-4 min-h-[350px] overflow-hidden ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} touch-pan-x touch-pan-y`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'none' }}
        >
          {/* Cream colored background */}
          <div className="absolute inset-0 bg-gradient-to-b from-orange-50 via-yellow-25 to-orange-100 rounded-lg"></div>
          
          {/* Ground */}
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-orange-200 to-orange-100 rounded-b-lg"></div>
          
          {/* Sun */}
          <div className="absolute top-4 right-8 w-12 h-12 bg-yellow-300 rounded-full opacity-80">
            <div className="absolute inset-2 bg-yellow-200 rounded-full"></div>
          </div>
          
          {/* Clouds */}
          <div className="absolute top-6 left-8 w-16 h-6 bg-white rounded-full opacity-60"></div>
          <div className="absolute top-8 left-12 w-12 h-4 bg-white rounded-full opacity-40"></div>
          <div className="absolute top-12 right-16 w-20 h-8 bg-white rounded-full opacity-50"></div>
          
          {/* Houses */}
          <div 
            className="relative h-full"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
          >
            {housePositions.map((house) => (
              <div
                key={house.id}
                className="absolute transform -translate-x-1/2 transition-all duration-500 hover:scale-110"
                style={{
                  left: `${house.x}%`,
                  top: `${house.y}%`,
                  transform: `translateX(-50%) translateY(-50%)`,
                }}
              >
                {/* House SVG */}
                <div className={`${house.isUser ? 'filter drop-shadow-lg' : ''}`}>
                  <svg
                    width={house.width}
                    height={house.height}
                    viewBox="0 0 40 50"
                    className="w-full h-full"
                    fill="none"
                  >
                    {/* House base */}
                    <rect
                      x="5"
                      y="25"
                      width="30"
                      height="20"
                      fill={house.isUser ? "#FFD700" : "#3B82F6"}
                      rx="2"
                    />
                    
                    {/* Roof */}
                    <polygon
                      points="20,10 2,25 38,25"
                      fill={house.isUser ? "#FF9800" : "#1E40AF"}
                    />
                    
                    {/* Door */}
                    <rect
                      x="16"
                      y="32"
                      width="8"
                      height="13"
                      fill={house.isUser ? "#8B4513" : "#374151"}
                      rx="1"
                    />
                    
                    {/* Windows */}
                    <rect
                      x="8"
                      y="28"
                      width="6"
                      height="6"
                      fill={house.isUser ? "#FFF3E0" : "#E5E7EB"}
                      rx="1"
                    />
                    <rect
                      x="26"
                      y="28"
                      width="6"
                      height="6"
                      fill={house.isUser ? "#FFF3E0" : "#E5E7EB"}
                      rx="1"
                    />
                    
                    {/* Window frames */}
                    <line x1="11" y1="28" x2="11" y2="34" stroke="#666" strokeWidth="0.5"/>
                    <line x1="8" y1="31" x2="14" y2="31" stroke="#666" strokeWidth="0.5"/>
                    <line x1="29" y1="28" x2="29" y2="34" stroke="#666" strokeWidth="0.5"/>
                    <line x1="26" y1="31" x2="32" y2="31" stroke="#666" strokeWidth="0.5"/>
                    
                    {/* Door handle */}
                    <circle cx="22" cy="38" r="0.8" fill="#666"/>
                  </svg>
                </div>
                
                {/* Tooltip for user houses */}
                {house.isUser && (
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full border border-yellow-300 opacity-0 hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                    Familie #{house.id + 1}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Legend */}
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-sm border">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 rounded bg-gradient-to-br from-yellow-400 to-orange-500"></div>
                <span className="text-gray-700 font-medium">Ihre Hilfe ({userHouses})</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-400 to-blue-600"></div>
                <span className="text-gray-600">Andere Hilfen ({totalHouses - userHouses})</span>
              </div>
            </div>
          </div>
        </div>
        
        {userHouses > 0 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-blue-700 font-medium">
              🏠 Vielen Dank für Ihre Unterstützung bedürftiger Familien! 
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Jede Ihrer Empfehlungen hilft dabei, Familien in schwierigen Zeiten zu unterstützen.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}